import React, { useMemo } from 'react';
import { AIChatInterface } from '../../shared/AIChatInterface';
import { TaskType } from '../../../../types/TaskType';

// Re-export ChatMessage type for backward compatibility
export type { ChatMessage } from '../../shared/AIChatInterface';

interface AIBreakdownChatProps {
  messages: any[];
  isLoading: boolean;
  userInput: string;
  onUserInputChange: (value: string) => void;
  onSendMessage: () => void;
  disabled?: boolean;
  onSendPrompt?: (prompt: string) => void;
  taskType: TaskType;
  hasBreakdown?: boolean;
  lastCompletedMode?: string | null;
  focusTriggerRef?: React.MutableRefObject<(() => void) | undefined>;
}

export const AIBreakdownChat: React.FC<AIBreakdownChatProps> = ({
  messages,
  isLoading,
  userInput,
  onUserInputChange,
  onSendMessage,
  disabled = false,
  onSendPrompt,
  taskType,
  hasBreakdown = false,
  lastCompletedMode = null,
  focusTriggerRef,
}) => {
  const defaultPromptText = useMemo(() => {
    return taskType === TaskType.epic ? 'Propose Epic breakdown to Items' : 'Propose Item breakdown to Subitems.';
  }, [taskType]);

  const suggestedActions = useMemo(() => {
    // Only show suggested actions if the last completed response included a breakdown update
    const shouldShowActions =
      hasBreakdown &&
      !isLoading &&
      onSendPrompt &&
      messages.length > 1 &&
      (lastCompletedMode === 'breakdown' || lastCompletedMode === 'both');

    if (!shouldShowActions) return null;

    const actions = ['Suggest new additions only', 'Improve what exists', 'Regenerate complete alternate set'];

    return (
      <div className="mt-3 flex flex-col gap-1 items-start">
        {actions.map((action) => (
          <button
            key={action}
            onClick={() => onSendPrompt(action)}
            className="bg-blue-100 rounded px-2 py-0.5 text-sm text-blue-600 font-normal leading-5 cursor-pointer border-none hover:bg-blue-200 transition-colors"
          >
            {action}
          </button>
        ))}
      </div>
    );
  }, [hasBreakdown, isLoading, onSendPrompt, messages.length, lastCompletedMode]);

  return (
    <AIChatInterface
      messages={messages}
      isLoading={isLoading}
      userInput={userInput}
      onUserInputChange={onUserInputChange}
      onSendMessage={onSendMessage}
      disabled={disabled}
      placeholder="Describe how to break down..."
      loadingMessage="Generating breakdown..."
      defaultPrompt={{
        text: defaultPromptText,
        buttonLabel: defaultPromptText,
      }}
      showDefaultPrompt={true}
      onSendPrompt={onSendPrompt}
      suggestedActions={suggestedActions}
      suggestedActionsPlacement="afterMessages"
      focusTriggerRef={focusTriggerRef}
    />
  );
};
